import { Accordion } from "@/components/ui/accordion";
import React from "react";
import { useLocale, useTranslations } from "../../../helpers/translations";
import { generateLocalizedPath } from "../helpers/links";
import { EXTERNAL_ROUTES, RAILS_ROUTES } from "../helpers/routes";
import { MobileNavSubSection } from "../shared/MobileNavSubSection";
import { NavSubMenuSection } from "../shared/NavSubMenuSection";

type ResourcesSubMenuProps = {
  compact?: boolean;
  wrapperComponent: React.ComponentType<{
    title: string;
    children: React.ReactNode;
  }>;
};

export const ResourcesSubMenu = ({
  compact,
  wrapperComponent,
}: ResourcesSubMenuProps) => {
  const t = useTranslations("nav.resources");
  const locale = useLocale();
  const WrapperComponent = wrapperComponent;

  if (!compact) {
    return (
      <WrapperComponent title={t("title")}>
        <NavSubMenuSection
          heading={t("get_inspired.header")}
          items={[
            {
              title: t("get_inspired.links.customer_stories.title"),
              href: generateLocalizedPath(RAILS_ROUTES.CUSTOMER_STORIES, locale),
            },
            {
              title: t("get_inspired.links.blog.title"),
              href: generateLocalizedPath(RAILS_ROUTES.BLOGS, locale),
              badge: t("get_inspired.links.blog.badge"),
            },
            {
              title: t("get_inspired.links.experience_center.title"),
              href: generateLocalizedPath(RAILS_ROUTES.EXPERIENCE_CENTER, locale),
            },
          ]}
        />
        <NavSubMenuSection
          heading={t("learn.header")}
          items={[
            {
              title: t("learn.links.tutorials.title"),
              href: generateLocalizedPath(RAILS_ROUTES.RESOURCES_VIDEO_TUTORIALS, locale),
            },
            {
              title: t("learn.links.training_webinars.title"),
              href: generateLocalizedPath(RAILS_ROUTES.WEBINARS, locale),
            },
            {
              title: t("learn.links.e_learning.title"),
              href: generateLocalizedPath(RAILS_ROUTES.RESOURCES_E_LEARNING, locale),
            },
          ]}
        />
        <NavSubMenuSection
          heading={t("tools_and_guides.header")}
          items={[
            {
              title: t("tools_and_guides.links.engineering_tools.title"),
              href: generateLocalizedPath(RAILS_ROUTES.TOOLS, locale),
            },
            {
              title: t("tools_and_guides.links.technical_documentation.title"),
              href: EXTERNAL_ROUTES.DOC360_TECH_DOC_KNOWLEDGE_BASE,
            },
            {
              title: t("tools_and_guides.links.api_docs.title"),
              href: EXTERNAL_ROUTES.DOCS_API,
            },
            {
              title: t("tools_and_guides.links.how_to_guides.title"),
              href: EXTERNAL_ROUTES.HOW_TO_GUIDES,
            },
            {
              title: t("tools_and_guides.links.automation_maturity_tool.title"),
              href: generateLocalizedPath(RAILS_ROUTES.AUTOMATION_MATURITY_ASSESSMENT, locale),
            },
          ]}
          cta={{
            href: EXTERNAL_ROUTES.DOCS_RELEASE_NOTES,
            text: t("tools_and_guides.cta.text"),
          }}
        />
        <NavSubMenuSection
          heading={t("support.header")}
          items={[
            {
              title: t("support.links.get_in_touch.title"),
              href: generateLocalizedPath(RAILS_ROUTES.CONTACT, locale),
            },
            {
              title: t("support.links.faqs.title"),
              href: generateLocalizedPath(RAILS_ROUTES.RESOURCES_FAQS, locale),
            },
          ]}
          cta={{
            href: generateLocalizedPath(RAILS_ROUTES.CONTACT, locale),
            text: t("support.cta.text"),
          }}
        />
      </WrapperComponent>
    );
  }

  return (
    <WrapperComponent title={t("title")}>
      <Accordion type="multiple" className="w-full border-none">
        <MobileNavSubSection
          title={t("get_inspired.header")}
          items={[
            {
              title: t("get_inspired.links.customer_stories.title"),
              href: generateLocalizedPath(RAILS_ROUTES.CUSTOMER_STORIES, locale),
            },
            {
              title: t("get_inspired.links.blog.title"),
              href: generateLocalizedPath(RAILS_ROUTES.BLOGS, locale),
              badge: t("get_inspired.links.blog.badge"),
            },
            {
              title: t("get_inspired.links.experience_center.title"),
              href: generateLocalizedPath(RAILS_ROUTES.EXPERIENCE_CENTER, locale),
            },
          ]}
        />
        <MobileNavSubSection
          title={t("learn.header")}
          items={[
            {
              title: t("learn.links.tutorials.title"),
              href: generateLocalizedPath(RAILS_ROUTES.RESOURCES_VIDEO_TUTORIALS, locale),
            },
            {
              title: t("learn.links.training_webinars.title"),
              href: generateLocalizedPath(RAILS_ROUTES.WEBINARS, locale),
            },
            {
              title: t("learn.links.e_learning.title"),
              href: generateLocalizedPath(RAILS_ROUTES.RESOURCES_E_LEARNING, locale),
            },
          ]}
        />
        <MobileNavSubSection
          title={t("tools_and_guides.header")}
          items={[
            {
              title: t("tools_and_guides.links.engineering_tools.title"),
              href: generateLocalizedPath(RAILS_ROUTES.TOOLS, locale),
            },
            {
              title: t("tools_and_guides.links.technical_documentation.title"),
              href: EXTERNAL_ROUTES.DOC360_TECH_DOC_KNOWLEDGE_BASE,
            },
            {
              title: t("tools_and_guides.links.api_docs.title"),
              href: EXTERNAL_ROUTES.DOCS_API,
            },
            {
              title: t("tools_and_guides.links.how_to_guides.title"),
              href: EXTERNAL_ROUTES.HOW_TO_GUIDES,
            },
            {
              title: t("tools_and_guides.links.automation_maturity_tool.title"),
              href: generateLocalizedPath(RAILS_ROUTES.AUTOMATION_MATURITY_ASSESSMENT, locale),
            },
          ]}
          cta={{
            text: t("tools_and_guides.cta.text"),
            href: EXTERNAL_ROUTES.DOCS_RELEASE_NOTES,
          }}
        />
        <MobileNavSubSection
          title={t("support.header")}
          items={[
            {
              title: t("support.links.get_in_touch.title"),
              href: generateLocalizedPath(RAILS_ROUTES.CONTACT, locale),
            },
            {
              title: t("support.links.faqs.title"),
              href: generateLocalizedPath(RAILS_ROUTES.RESOURCES_FAQS, locale),
            },
          ]}
          cta={{
            text: t("support.cta.text"),
            href: generateLocalizedPath(RAILS_ROUTES.CONTACT, locale),
          }}
        />
      </Accordion>
    </WrapperComponent>
  );
};
